/**
 * User Geo
 * Read user geolocation data from cache
 */

/**
 * @typedef {Object} UserGeo
 * @property {number} [lat]
 * @property {number} [lng]
 * @property {string} [city]
 * @property {string} [state]
 * @property {boolean} [isUS]
 * @property {boolean} [inEurope]
 */

/**
 * Read user geo from localStorage cache.
 * For non-US users, always return San Rafael coordinates (not real user coordinates).
 * For US users, use real IP coordinates (from realLocationData), not coordinates from previous geocode.
 * @returns {UserGeo|null}
 */
export function readUserGeoFromCache() {
  const defaults = window.CampsUtils?.defaults || {};
  const cacheKey = defaults.cacheKey || 'campsNearYou_geoLocation_v4';
  try {
    const raw = localStorage.getItem(cacheKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);

    // Check if user is non-US (from realLocationData, not from coords which may be updated by geocode)
    const userIsUS =
      parsed.realLocationData?.isUS !== undefined
        ? parsed.realLocationData.isUS
        : parsed.coords?.isUS !== undefined
        ? parsed.coords.isUS
        : true; // default to US if unknown

    // Check if user is in Europe (from realLocationData or coords)
    const userInEurope =
      parsed.realLocationData?.inEurope ||
      parsed.coords?.inEurope ||
      false;

    if (userIsUS === false) {
      // Non-US user: always use San Rafael coordinates, not real coordinates
      return {
        lat: defaults.fallbackCoords.lat,
        lng: defaults.fallbackCoords.lng,
        city: '',
        state: '',
        isUS: false,
        inEurope: userInEurope,
      };
    }

    // US user: use real IP coordinates from realLocationData (not from coords which may be updated by geocode)
    const realLocation = parsed.realLocationData;
    if (
      realLocation &&
      typeof realLocation.lat === 'number' &&
      typeof realLocation.lng === 'number'
    ) {
      return {
        lat: realLocation.lat,
        lng: realLocation.lng,
        city: realLocation.city || '',
        state: realLocation.regionCode || '',
        isUS: true,
        inEurope: false, // US users are not in Europe
      };
    }

    // Fallback: use coords if realLocationData not available
    const { lat, lng, city, region, regionCode } = parsed.coords || parsed;
    if (typeof lat !== 'number' || typeof lng !== 'number') return null;
    return {
      lat,
      lng,
      city: parsed.city || region || parsed.region || city || '',
      state: parsed.state || parsed.regionCode || regionCode || '',
      isUS: true,
      inEurope: false, // Default to false for fallback
    };
  } catch (e) {
    return null;
  }
}

console.log('📦 User geo module loaded');
